/**
 * <CookieSettingsButton> — "Cookie settings": reopens the consent banner, so a visitor can change a choice they
 * already made (LIFE-SHORTLISTED-001, the shopper privacy surface, owner ruling G6). Until it existed a choice
 * stood for 12 months with no way back short of clearing the site's storage.
 *
 * It renders NOTHING where there is nothing to reopen, so a footer can render it unconditionally:
 * - no consent provider in the tree (the server-rendered blog);
 * - a surface that cannot ask for consent (`ConsentWithheldProvider`);
 * - before the stored choice has been read (so the server and the first client render agree);
 * - while the banner is already asking.
 *
 * Its word is marketing-shell's own (`cookieConsent.settings`).
 */

import PropTypes from 'prop-types';
import { useOptionalCookieConsent } from './CookieConsent.jsx';
import { useMarketingShellTranslate } from '../hooks/useMarketingShellTranslate.js';

/**
 * Whether a consent context has a choice the visitor could change right now — the ONE rule the button and a
 * footer laying out around it share.
 *
 * @param {?{canReopen: boolean, showBanner: boolean}} consent - `useOptionalCookieConsent()`
 * @returns {boolean}
 */
export function canReopenConsent(consent) {
  return Boolean(consent) && consent.canReopen && !consent.showBanner;
}

/**
 * @param {Object} props
 * @param {string} [props.className='btn btn-link'] - the surface's own register (the footer passes its legal
 *   row's action class)
 * @returns {React.JSX.Element|null}
 */
export function CookieSettingsButton({ className = 'btn btn-link' }) {
  const consent = useOptionalCookieConsent();
  const t = useMarketingShellTranslate();
  if (!canReopenConsent(consent)) {
    return null;
  }
  return (
    <button type="button" className={className} onClick={consent.reopenBanner}>
      {t('cookieConsent.settings')}
    </button>
  );
}

CookieSettingsButton.propTypes = {
  className: PropTypes.string,
};

export default CookieSettingsButton;
